import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CalendarDays, Eye, TrendingUp, UserCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const PATH_LABELS = {
  "/": "Home",
  "/gallery": "Gallery",
  "/committee": "Committee",
  "/sponsors": "Sponsors",
  "/live-results": "Live Results",
  "/payment/success": "Payment Success",
  "/payment/cancel": "Payment Cancelled",
};

export default function AdminStats({ token }) {
  const [data, setData] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/analytics", { headers });
      setData(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load site stats");
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load();
  }, [load]);

  const cards = [
    { icon: Eye, label: "Total Visits", value: data?.total_views ?? "—", testid: "stat-total-visits" },
    { icon: TrendingUp, label: "Visits Today", value: data?.today_views ?? "—", testid: "stat-today-visits" },
    { icon: UserCheck, label: "Unique Visitors Today", value: data?.today_uniques ?? "—", testid: "stat-today-uniques" },
    { icon: CalendarDays, label: "Last 7 Days", value: data ? `${data.week_views} (${data.week_uniques} unique)` : "—", testid: "stat-week-visits" },
  ];

  const maxViews = Math.max(1, ...(data?.daily || []).map((d) => d.views));

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-10 space-y-12">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {cards.map((s, i) => (
          <motion.div
            key={s.label}
            data-testid={s.testid}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.08 }}
            className="border border-clay-border bg-clay-card p-6"
          >
            <s.icon className="w-6 h-6 text-clay-orange mb-4" />
            <p className="font-display font-black text-4xl">{s.value}</p>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-clay-muted mt-2">{s.label}</p>
          </motion.div>
        ))}
      </div>

      <section>
        <h2 className="font-display font-extrabold uppercase text-3xl tracking-tight mb-5">
          Daily Visits <span className="text-clay-muted text-xl">(last 14 days)</span>
        </h2>
        <div className="border border-clay-border bg-clay-card p-6">
          {(!data || data.daily.length === 0) && (
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-clay-muted py-8 text-center">
              No visits recorded yet — counting started today
            </p>
          )}
          <div className="space-y-2">
            {(data?.daily || []).map((d) => (
              <div key={d.date} data-testid="stats-day-row" className="grid grid-cols-[90px_1fr_auto] items-center gap-4">
                <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-clay-muted">
                  {new Date(`${d.date}T00:00:00`).toLocaleDateString("en-AU", { day: "2-digit", month: "short" })}
                </span>
                <div className="h-6 bg-clay-bg border border-clay-border/60">
                  <div
                    className="h-full bg-clay-orange transition-all duration-500"
                    style={{ width: `${(d.views / maxViews) * 100}%` }}
                  />
                </div>
                <span className="font-mono text-xs text-clay-text w-28 text-right">
                  {d.views} <span className="text-clay-muted">({d.uniques} uniq)</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section>
        <h2 className="font-display font-extrabold uppercase text-3xl tracking-tight mb-5">Top Pages</h2>
        <div className="border border-clay-border">
          {(!data || data.top_pages.length === 0) && (
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-clay-muted py-12 text-center">No page data yet</p>
          )}
          {(data?.top_pages || []).map((p) => (
            <div
              key={p.path}
              data-testid="stats-page-row"
              className="flex items-center justify-between border-b border-clay-border/60 last:border-b-0 px-4 sm:px-6 py-4 hover:bg-clay-card/60 transition-colors"
            >
              <span className="font-display font-bold uppercase tracking-tight">
                {PATH_LABELS[p.path] || p.path}
                {PATH_LABELS[p.path] && <span className="text-clay-muted text-xs font-mono ml-3 normal-case tracking-normal">{p.path}</span>}
              </span>
              <span className="font-mono text-sm text-clay-orange">{p.views} visits</span>
            </div>
          ))}
        </div>
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-clay-muted mt-4">
          Counting since {data?.since ? new Date(`${data.since}T00:00:00`).toLocaleDateString("en-AU", { day: "2-digit", month: "long", year: "numeric" }) : "today"} — admin visits are not counted, and no personal info is stored
        </p>
      </section>
    </div>
  );
}
